import React, { useEffect, useImperativeHandle, useState } from 'react';
import { Col, Form, Row } from 'antd';
import moment from 'moment';
import { Rule } from 'antd/lib/form';
import { FormInstance } from 'antd/es/form';
import intl from 'react-intl-universal';
import getRandomId from '@/utils/uuid';
import DragDropContainer from './DragDropContainer';
import styles from './index.module.scss';

export interface DragFieldRef extends FormInstance {
  addFn?: (..._) => void;
}

export interface DynamicFieldItem {
  label: string;
  span: number;
  key: string;
  type?: 'dateTime' | 'input' | 'textArea' | 'inputMoney';
  rules?: Rule[];
  fieldProps?: {
    onBlur?: (value: any, index: number) => void;
  };
}
export interface DragFieldListProps {
  dynamicFormAttr: Array<DynamicFieldItem>;
  // fieldList键值
  fieldListName?: string;
  dynamicDataSource?: Array<any>;
  myRef?: any;
  onValuesChange?: (..._) => void;
}

const DragFieldList: React.FC<DragFieldListProps> = ({
  dynamicFormAttr,
  fieldListName = 'fieldList',
  dynamicDataSource,
  myRef,
  onValuesChange,
}) => {
  const [quotes, setQuotes] = useState<any>([]);
  const [formListInstance] = Form.useForm();

  const handleAdd = (list) => {
    // 生成唯一ID
    const defaultValue = { id: getRandomId() };
    setQuotes([...list, defaultValue]);
    formListInstance.setFieldsValue({ [fieldListName]: [...list, defaultValue] });
  };

  const handleRemove = (removeFn, name) => {
    const tempQuotes = [...quotes];
    tempQuotes.splice(name, 1);
    setQuotes(tempQuotes);
    if (typeof removeFn === 'function') {
      removeFn(name);
    }
  };

  const onDrapChange = (dataSource) => {
    setQuotes(dataSource);
    formListInstance.setFieldsValue({ [fieldListName]: dataSource });
  };

  const handleValuesChange = (changeValues, allValues) => {
    if (onValuesChange) {
      onValuesChange(changeValues, allValues);
    }
  };

  useEffect(() => {
    if (fieldListName) {
      // 把时间戳转换成moment
      const dateKeyList = dynamicFormAttr
        ?.filter((item) => item.type === 'dateTime')
        ?.map((item) => item.key);
      let dataSource = [...dynamicDataSource];
      dataSource = dataSource?.map((item) => {
        const tempItem = { ...item };
        tempItem.id = item.id ?? getRandomId();
        if (Array.isArray(dateKeyList) && dateKeyList.length) {
          dateKeyList?.forEach((key) => {
            tempItem[key] = tempItem[key] ? moment(tempItem[key]) : undefined;
          });
        }
        return tempItem;
      });
      setQuotes(dataSource);
      formListInstance?.setFieldsValue({ [fieldListName]: dataSource });
    }
  }, [dynamicFormAttr, fieldListName, dynamicDataSource]);

  useImperativeHandle(
    myRef,
    () => ({
      ...formListInstance,
      addFn: () => handleAdd(quotes),
    }),
    [quotes],
  );

  return (
    <Form
      className={styles.dynamicHorizontalForm}
      name={`dynamic_form${getRandomId()}`}
      form={formListInstance}
      onValuesChange={handleValuesChange}
      autoComplete="off"
    >
      <Form.List name={fieldListName}>
        {(fields, { remove }, { errors }) => (
          <>
            {/* 动态表单标题 */}
            {dynamicFormAttr?.length && (
              <Row className={styles.headRow} gutter={[0, 0]}>
                <Col key={getRandomId()} span={2}>
                  {intl.get('cpm_custom_form_drag_sort')}
                </Col>
                {dynamicFormAttr?.map((item) => (
                  <Col key={item.key} span={item.span}>
                    {item.label}
                  </Col>
                ))}
                <Col key={getRandomId()} span={1}>
                  {intl.get('app_table_column_action')}
                </Col>
              </Row>
            )}
            <DragDropContainer
              dynamicFormAttr={dynamicFormAttr}
              fields={fields}
              removeFn={(name) => handleRemove(remove, name)}
              quotes={quotes}
              onDrapChange={onDrapChange}
              listStyle={{ padding: '0px' }}
            />

            <Form.Item>
              <Form.ErrorList errors={errors} />
            </Form.Item>
          </>
        )}
      </Form.List>
    </Form>
  );
};

export default React.forwardRef<DragFieldRef, DragFieldListProps>((props, ref) => (
  <DragFieldList {...props} myRef={ref} />
));
